import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import {
  mirrorToNative,
  removeFromNative,
  ACCESS_KEY,
  REFRESH_KEY,
  MFA_DEVICE_KEY,
  DEVICE_ID_KEY,
} from '../tokenStore';

const BACKEND_URL: string = process.env.REACT_APP_BACKEND_URL!;
const API: string = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API, withCredentials: true });

// --- Token / device persistence --------------------------------------------
// Mobile (Capacitor) WebViews run on a different origin than the API, so the
// backend's HttpOnly cookies are third-party and get dropped by the OS. We
// therefore keep the access token, refresh token and MFA "remember this device"
// token in localStorage and send them back via headers / request body. Web keeps
// working via cookies too — this is purely additive.
export function getDeviceId(): string {
  let id = localStorage.getItem('dutchdate_device_id');
  if (!id) {
    id = (window.crypto?.randomUUID?.() ?? `dd-${Date.now()}-${Math.random().toString(36).slice(2)}`);
    localStorage.setItem('dutchdate_device_id', id);
    mirrorToNative(DEVICE_ID_KEY, id);
  }
  return id;
}

export function persistAuthTokens(data: any): void {
  if (!data) return;
  if (data.token) { localStorage.setItem('dutchdate_token', data.token); mirrorToNative(ACCESS_KEY, data.token); }
  if (data.refresh_token) { localStorage.setItem('dutchdate_refresh', data.refresh_token); mirrorToNative(REFRESH_KEY, data.refresh_token); }
  if (data.device_token) { localStorage.setItem('dutchdate_mfa_device', data.device_token); mirrorToNative(MFA_DEVICE_KEY, data.device_token); }
}

export function clearAuthTokens(): void {
  localStorage.removeItem('dutchdate_token');
  localStorage.removeItem('dutchdate_refresh');
  // Explicit logout must fully clear the SESSION tokens from durable native
  // storage too (not just localStorage), or a stale token could rehydrate on
  // the next cold start.
  removeFromNative(ACCESS_KEY);
  removeFromNative(REFRESH_KEY);
  // Intentionally KEEP dutchdate_device_id and dutchdate_mfa_device so a user who
  // manually logs out isn't re-challenged with MFA on their trusted device within
  // the 30-day remember window.
}

// Request interceptor — attach access token + stable device identity
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = localStorage.getItem('dutchdate_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  config.headers['X-Device-Id'] = getDeviceId();
  const mfaDevice = localStorage.getItem('dutchdate_mfa_device');
  if (mfaDevice) config.headers['X-Device-Token'] = mfaDevice;
  return config;
});

// Response interceptor — auto-refresh on 401, step-up on 403 step_up_required
let refreshPromise: Promise<string> | null = null;
api.interceptors.response.use(
  (res) => res,
  async (error: AxiosError) => {
    // First, capture the structured step-up code BEFORE normalizing
    const rawDetail: any = (error.response?.data as any)?.detail;
    const stepUpCode =
      rawDetail && typeof rawDetail === 'object' && !Array.isArray(rawDetail)
        ? rawDetail.code
        : null;

    // Normalize structured FastAPI errors so call-sites can always do
    // `toast.error(err.response.data.detail)` without crashing React on objects.
    if (error.response?.data) {
      const data: any = error.response.data;
      if (data.detail && typeof data.detail === 'object') {
        if (data.detail.message) {
          // Step-up shape: {code, reason, message} → expose message as string
          data.detail = data.detail.message;
        } else if (Array.isArray(data.detail)) {
          // Pydantic validation: [{loc, msg, type}, ...]
          data.detail = data.detail.map((d: any) => d?.msg || JSON.stringify(d)).join('; ');
        } else {
          data.detail = JSON.stringify(data.detail);
        }
      }
    }

    // Login-time step-up: surface it to the global modal so admins/users can verify.
    // Extract the login email from the failed request body so the modal can
    // request a code even when `useAuth().user` is still null at this point.
    if (error.response?.status === 403 && stepUpCode === 'step_up_required') {
      let email: string | undefined;
      try {
        const cfg: any = error.config;
        if (cfg?.data) {
          const body = typeof cfg.data === 'string' ? JSON.parse(cfg.data) : cfg.data;
          if (body && typeof body.email === 'string') email = body.email;
        }
      } catch { /* best-effort */ }
      window.dispatchEvent(new CustomEvent('auth:step-up-required', { detail: { email } }));
    }

    // AI consent gate: backend returns 403 with `code=AI_CONSENT_REQUIRED` for
    // any /api/ai/* call before the user has accepted the disclosure. Surface
    // a global event so the AIConsentModal mounted in AppLayout can open
    // itself and the caller can retry after acceptance.
    if (error.response?.status === 403 && stepUpCode === 'AI_CONSENT_REQUIRED') {
      // Passive/background AI calls (e.g. the dashboard affirmation banner) opt
      // out of the global prompt so they don't stack on top of the first-login
      // legal-consent modals. The AI consent modal still opens for explicit
      // user-invoked AI features.
      const silent = (error.config?.headers?.['X-Silent-AI-Consent'] === '1');
      if (!silent) {
        window.dispatchEvent(new CustomEvent('ai:consent-required'));
      }
    }

    const original = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    if (
      error.response?.status === 401 &&
      !original._retry &&
      !original.url?.includes('/auth/refresh') &&
      !original.url?.includes('/auth/login')
    ) {
      original._retry = true;
      if (!refreshPromise) {
        const mfaDevice = localStorage.getItem('dutchdate_mfa_device');
        refreshPromise = axios
          .post(
            `${API}/auth/refresh`,
            { refresh_token: localStorage.getItem('dutchdate_refresh') || undefined },
            {
              withCredentials: true,
              headers: {
                'X-Device-Id': getDeviceId(),
                ...(mfaDevice ? { 'X-Device-Token': mfaDevice } : {}),
              },
            },
          )
          .then((res) => {
            const newToken: string = res.data.token;
            localStorage.setItem('dutchdate_token', newToken);
            mirrorToNative(ACCESS_KEY, newToken);
            if (res.data.refresh_token) {
              localStorage.setItem('dutchdate_refresh', res.data.refresh_token);
              mirrorToNative(REFRESH_KEY, res.data.refresh_token);
            }
            return newToken;
          })
          .catch((err) => {
            const status = err?.response?.status;
            const detail = err?.response?.data?.detail;
            const code = typeof detail === 'object' ? detail?.code : null;
            if (status === 403 && code === 'step_up_required') {
              // Suspicious device — surface step-up but KEEP the session/token;
              // a step-up must never silently log the user out.
              window.dispatchEvent(new CustomEvent('auth:step-up-required'));
            } else if (status === 401) {
              // Definitive: the refresh token itself is invalid/expired → the
              // session is genuinely dead, so clear it from both stores.
              localStorage.removeItem('dutchdate_token');
              localStorage.removeItem('dutchdate_refresh');
              removeFromNative(ACCESS_KEY);
              removeFromNative(REFRESH_KEY);
              window.dispatchEvent(new Event('auth:expired'));
            }
            // else: transient (offline / no response / 5xx) → keep the tokens and
            // just fail this one call; the session survives and retries later.
            return Promise.reject(err);
          })
          .finally(() => {
            refreshPromise = null;
          });
      }
      const newToken = await refreshPromise;
      original.headers.Authorization = `Bearer ${newToken}`;
      return api(original);
    }
    return Promise.reject(error);
  }
);

export { BACKEND_URL, API };
export default api;
